// SPDX-License-Identifier: AGPL-3.0-or-later
import { useMemo, useRef, useState } from "react";
import { categorize, previewImport, STARTER_RULES, type ColumnMapping, type DateOrder, type ImportPreview } from "@almanac/importers";
import rbcSample from "../../../../packages/importers/fixtures/sample-rbc.csv?raw";
import tangerineSample from "../../../../packages/importers/fixtures/sample-tangerine.csv?raw";
import scotiaSample from "../../../../packages/importers/fixtures/sample-scotiabank.csv?raw";
import eqSample from "../../../../packages/importers/fixtures/sample-eq-bank.csv?raw";
import { useApp } from "../lib/app.js";
import { useI18n } from "../lib/i18n.js";
import { CategoryOptions } from "../components/CategoryOptions.js";
import { useStore } from "../lib/store.js";

const SAMPLES = [
  { name: "RBC", text: rbcSample },
  { name: "Tangerine", text: tangerineSample },
  { name: "Scotiabank", text: scotiaSample },
  { name: "EQ Bank", text: eqSample },
];

const SHOWN_ROWS = 250;

export function Import() {
  const { store, announce } = useApp();
  const state = useStore(store);
  const { t, money, date, category } = useI18n();
  const [text, setText] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [mapping, setMapping] = useState<ColumnMapping | undefined>(undefined);
  const [account, setAccount] = useState("");
  const [overrides, setOverrides] = useState<Record<number, string>>({});
  const [over, setOver] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const learned = useMemo(() => new Map(Object.entries(state.learned)), [state.learned]);

  const preview: ImportPreview | null = useMemo(
    () => (text === null ? null : previewImport(text, state.fingerprints, mapping)),
    [text, mapping, state.fingerprints],
  );

  const load = (contents: string, name: string, accountName = "") => {
    setText(contents);
    setFileName(name);
    setMapping(undefined);
    setOverrides({});
    setDone(null);
    const detected = previewImport(contents).profile;
    setAccount(accountName || detected?.name || "");
  };

  const readFile = async (file: File | undefined) => {
    if (file) load(await file.text(), file.name);
  };

  const categoryFor = (i: number, description: string, amount: number) =>
    overrides[i] ?? categorize(description, amount, STARTER_RULES, learned).category ?? "";

  const fresh = preview?.transactions.filter((tx) => !tx.duplicate) ?? [];

  return (
    <>
      <p>{t("import.intro")}</p>
      <div
        className="dropzone card"
        data-over={over}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void readFile(e.dataTransfer.files[0]);
        }}
      >
        <strong>{t("import.drop")}</strong>
        <input
          ref={fileInput}
          id="csv-file"
          type="file"
          accept=".csv,text/csv"
          className="visually-hidden"
          onChange={(e) => void readFile(e.target.files?.[0])}
        />
        <button type="button" className="primary" onClick={() => fileInput.current?.click()}>
          {t("import.choose")}
        </button>
        <p className="hint">{t("import.privacy")}</p>
        <div className="row" role="group" aria-label={t("import.sample")}>
          <span className="hint">{t("import.sample")}</span>
          {SAMPLES.map((s) => (
            <button key={s.name} type="button" onClick={() => load(s.text, `sample-${s.name}.csv`, `${s.name} (sample)`)}>
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {done && (
        <p role="status" className="card" style={{ marginTop: 16 }}>
          {done}
        </p>
      )}

      {preview && !done && (
        <section className="card" style={{ marginTop: 16 }} aria-labelledby="preview-title">
          <h2 id="preview-title">{fileName}</h2>
          {preview.profile ? (
            <p>
              {t("import.detected", { bank: preview.profile.name })}{" "}
              {!preview.profile.verified && <span className="hint">{t("import.unverified", { bank: preview.profile.name })}</span>}
            </p>
          ) : !mapping ? (
            <MappingForm rows={preview.rows} onApply={setMapping} />
          ) : null}

          {preview.mapping && (
            <>
              <p role="status">
                {t("import.summary", {
                  count: preview.transactions.length,
                  fresh: fresh.length,
                  duplicates: preview.transactions.length - fresh.length,
                  errors: preview.errors.length,
                })}
              </p>
              {preview.errors.length > 0 && (
                <ul className="hint">
                  {preview.errors.slice(0, 10).map((e) => (
                    <li key={e.line}>{t("import.error", { line: e.line, message: e.message })}</li>
                  ))}
                </ul>
              )}
              <div className="field" style={{ maxWidth: 360 }}>
                <label htmlFor="import-account">{t("import.account")}</label>
                <input id="import-account" type="text" list="known-accounts" value={account} onChange={(e) => setAccount(e.target.value)} />
                <datalist id="known-accounts">
                  {state.accounts.map((a) => (
                    <option key={a.id} value={a.name} />
                  ))}
                </datalist>
              </div>
              <div className="table-wrap">
                <table className="data">
                  <caption className="visually-hidden">{fileName}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t("import.col.date")}</th>
                      <th scope="col">{t("import.col.description")}</th>
                      <th scope="col" className="num">
                        {t("import.col.amount")}
                      </th>
                      <th scope="col">{t("import.col.category")}</th>
                      <th scope="col">{t("import.col.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.transactions.slice(0, SHOWN_ROWS).map((tx, i) => {
                      const cat = categoryFor(i, tx.description, tx.amount);
                      return (
                        <tr key={`${tx.fingerprint}-${i}`} data-duplicate={tx.duplicate}>
                          <td>{date(tx.date)}</td>
                          <td>{tx.description}</td>
                          <td className={`num ${tx.amount < 0 ? "negative" : "positive"}`}>{money(tx.amount)}</td>
                          <td>
                            <select
                              aria-label={`${t("import.col.category")}: ${tx.description}`}
                              value={cat}
                              disabled={tx.duplicate}
                              onChange={(e) => setOverrides({ ...overrides, [i]: e.target.value })}
                            >
                              <option value="">{t("import.needsCategory")}</option>
                              <CategoryOptions />
                            </select>
                          </td>
                          <td>{tx.duplicate ? t("import.duplicate") : t("import.new")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="row" style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="primary"
                  disabled={fresh.length === 0 || !account.trim()}
                  onClick={() => {
                    const target = store.ensureAccount(account, preview.profile?.id ?? null);
                    // Corrections made here teach Almanac the payee for next time.
                    for (const [i, cat] of Object.entries(overrides)) {
                      const tx = preview.transactions[Number(i)];
                      if (tx && cat) store.learn(tx.description, cat);
                    }
                    const added = store.addTransactions(
                      target.id,
                      preview.transactions
                        .map((tx, i) => ({ tx, i }))
                        .filter(({ tx }) => !tx.duplicate)
                        .map(({ tx, i }) => ({
                          date: tx.date,
                          amount: tx.amount,
                          description: tx.description,
                          category: categoryFor(i, tx.description, tx.amount) || null,
                          fingerprint: tx.fingerprint,
                        })),
                    );
                    const message = t("import.done", { count: added });
                    setDone(message);
                    announce(message);
                    setText(null);
                  }}
                >
                  {t("import.confirm", { count: fresh.length })}
                </button>
                <button type="button" onClick={() => setText(null)}>
                  {t("common.cancel")}
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </>
  );
}

function MappingForm({ rows, onApply }: { rows: string[][]; onApply: (m: ColumnMapping) => void }) {
  const { t } = useI18n();
  const width = Math.max(0, ...rows.slice(0, 5).map((r) => r.length));
  const [hasHeader, setHasHeader] = useState(true);
  const [dateCol, setDateCol] = useState(0);
  const [descCol, setDescCol] = useState(Math.min(1, width - 1));
  const [amountCol, setAmountCol] = useState(Math.min(2, width - 1));
  const [order, setOrder] = useState<DateOrder>("MDY");
  const columns = Array.from({ length: width }, (_, i) => ({ i, label: `${i + 1}: ${rows[0]?.[i] ?? ""}`.slice(0, 40) }));
  const select = (id: string, label: string, value: number, set: (n: number) => void) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => set(Number(e.target.value))}>
        {columns.map((c) => (
          <option key={c.i} value={c.i}>
            {c.label}
          </option>
        ))}
      </select>
    </div>
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onApply({ hasHeader, dateOrder: order, date: dateCol, description: [descCol], amount: amountCol });
      }}
    >
      <p>{t("import.unknown")}</p>
      <div className="table-wrap" style={{ marginBottom: 12 }}>
        <table className="data">
          <tbody>
            {rows.slice(0, 4).map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <label className="row" style={{ fontWeight: 450, marginBottom: 12 }}>
        <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
        {t("import.hasHeader")}
      </label>
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        {select("map-date", t("import.mapDate"), dateCol, setDateCol)}
        {select("map-description", t("import.mapDescription"), descCol, setDescCol)}
        {select("map-amount", t("import.mapAmount"), amountCol, setAmountCol)}
        <div className="field">
          <label htmlFor="map-order">{t("import.mapDateOrder")}</label>
          <select id="map-order" value={order} onChange={(e) => setOrder(e.target.value as DateOrder)}>
            <option value="MDY">MM/DD/YYYY</option>
            <option value="DMY">DD/MM/YYYY</option>
            <option value="YMD">YYYY-MM-DD</option>
          </select>
        </div>
      </div>
      <button type="submit" className="primary">
        {t("import.mapApply")}
      </button>
    </form>
  );
}
